/**
 * @fileoverview Floating action bar for ProductAdminPage.
 * Appears when product or variant forms have unsaved changes.
 * @module features/products/components/ProductActionBar
 */

import type { ReactElement } from 'react'
import { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Loader2, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { cn } from '@/shared/lib/utils'

export interface ProductActionBarProps {
  /** Whether there are unsaved changes */
  hasChanges: boolean
  /** Whether save operation is in progress */
  isSaving: boolean
  /** Description of what has changed */
  changeDescription?: string | undefined
  /** Callback to save all changes */
  onSave: () => void
  /** Callback to discard all changes */
  onCancel: () => void
  /** Optional className for outer container */
  className?: string
}

/**
 * Top floating action bar displayed when product/variant has unsaved changes.
 * Uses solid background matching SelectionActionBar styling.
 */
export function ProductActionBar({
  hasChanges,
  isSaving,
  changeDescription,
  onSave,
  onCancel,
  className,
}: ProductActionBarProps): ReactElement | null {
  const { t } = useTranslation('features-products')

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!hasChanges) return

    const handleKeyDown = (event: KeyboardEvent): void => {
      // Escape key: Cancel/discard changes
      if (event.key === 'Escape' && !isSaving) {
        event.preventDefault()
        onCancel()
      }

      // Enter: Save changes
      if (event.key === 'Enter') {
        event.preventDefault()
        onSave()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [hasChanges, isSaving, onSave, onCancel])

  return (
    <AnimatePresence>
      {hasChanges && (
        <motion.div
          role="region"
          aria-live="polite"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ duration: 0.24, ease: 'easeOut' }}
          className={cn(
            'fixed top-0 left-0 lg:left-20 right-0 z-40 bg-card border-b border-border shadow-md',
            className
          )}
        >
          <div className="max-w-screen-lg mx-auto px-4 lg:px-8 py-4 flex items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="ghost"
                size="lg"
                onClick={onCancel}
                disabled={isSaving}
                aria-label={t('actionBar.discardChanges')}
                className="gap-2"
              >
                {t('actionBar.discardChanges')}
                <Kbd>Esc</Kbd>
              </Button>
              <span className="text-sm text-muted-foreground">
                {changeDescription ?? t('actionBar.unsavedChanges')}
              </span>
            </div>

            <Button
              type="button"
              size="lg"
              onClick={onSave}
              disabled={isSaving}
              className="ml-auto gap-2"
              data-save-action="true"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isSaving ? t('actionBar.saving') : t('actionBar.saveChanges')}
              {!isSaving && (
                <Kbd className="bg-primary-foreground/20 text-primary-foreground/80 border border-primary-foreground/30">
                  Enter
                </Kbd>
              )}
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ProductActionBar
